// src/components/core/HeadersAndFooters/MobileActionLine.jsx
//
// Shared mobile front-page action line (the "Row 3" of the mobile FP header): a context-coloured
// 40px strip carrying a Create pill on the left and an inline-expanding search on the right.
//
// Extracted verbatim from the locality front-page header (HeaderButtonsFeed isMobileFP branch) so
// the locality and group front pages use ONE implementation — same style, geometry, and the
// grow-from-the-magnifier search behaviour. The create action and label, the search handler, and
// the context colour are injected; the component owns its own search open/close state, focus, and
// outside-tap dismiss. Reuse, not a parallel re-build (Mobile Groups, Commit 6).

import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { tokens } from '../../../styles/designTokens';

const MobileActionLine = ({
  contextColor,
  createLabel,
  onCreate,
  createDisabled = false,
  center = null,          // optional element between create and search (e.g. Events' Future/Past
                           // toggle) — pure layout slot, no logic of its own. When the search field
                           // expands it still covers everything to its left, center included, same
                           // as it already covers create today.
  topBorder = true,       // frames against the active tab above (mobile-tab-subheaders addendum
                           // 2). MobileGroupFrontPage's own call site passes false to keep its
                           // pre-existing look: a different tab bar, out of scope, already
                           // buffered by its own unrelated design.
  sideBorders = true,     // left/right border of its own. The five Locality-composite consumers
                           // pass false (addendum 3, fault 2): drawing its own L/R border AND
                           // sitting inside the outer viewport frame's L/R border doubled the
                           // apparent width there specifically. Those five now rely entirely on
                           // that outer frame for L/R/bottom — this component contributes only
                           // its white fill. MobileGroupFrontPage keeps the default (true),
                           // unaffected — out of scope, no fault reported there.
  searchPlaceholder,
  onSearch
}) => {
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Focus the field when it expands; close on an outside tap (no backdrop/dimming).
  useEffect(() => {
    if (!showSearch) return;
    inputRef.current?.focus();
    const handleOutsidePointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => document.removeEventListener('pointerdown', handleOutsidePointer);
  }, [showSearch]);

  return (
    <div style={{
      position: 'relative',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 12px',
      background: 'white',
      // topBorder/sideBorders default true for MobileGroupFrontPage's own call site (unchanged,
      // out of scope). The five Locality-composite consumers pass both false (mobile-tab-
      // subheaders addendum 3): they contribute no border of their own at all, relying entirely
      // on the caller's own outer frame for the whole enclosed region's L/R/bottom/notched-top
      // border, avoiding a doubled line where this component used to nest inside that frame.
      borderTop: topBorder ? `2px solid ${contextColor}` : 'none',
      borderLeft: sideBorders ? `2px solid ${contextColor}` : 'none',
      borderRight: sideBorders ? `2px solid ${contextColor}` : 'none',
      boxSizing: 'border-box'
    }}>
      {/* Create — text button; sits in the flow underneath; the expanding search covers it when
          open. Omitted entirely when no onCreate is provided (e.g. Directory, whose create
          affordance lives in ModeColumn's pinned footer slot instead — see DirectoryColumn.jsx). */}
      {onCreate && (
        <button
          onClick={onCreate}
          disabled={createDisabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: createDisabled ? 'default' : 'pointer',
            fontFamily: tokens.typography.fontFamily,
            fontSize: '13px',
            fontWeight: 600,
            color: createDisabled ? tokens.colors.disabledText : contextColor
          }}
        >
          <Plus size={16} strokeWidth={2.5} />
          <span>{createLabel}</span>
        </button>
      )}

      {/* Optional centred content (e.g. Events' Future/Past toggle) — takes the remaining space
          between create and the search anchor, centring its own children within it. */}
      {center && (
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          {center}
        </div>
      )}

      {/* Inline search — anchored right; the magnifier is the fixed anchor and the field grows
          leftward from it. Omitted entirely when no onSearch is provided. */}
      {onSearch && (
        <div
          ref={wrapRef}
          style={{
            position: 'absolute',
            right: '12px',
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            background: 'white'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            width: showSearch ? 'calc(100vw - 56px)' : '0px',
            height: '28px',
            background: tokens.colors.headerBackground,
            borderRadius: '14px',
            transition: 'width 0.25s ease',
            marginRight: showSearch ? '6px' : '0px'
          }}>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              onKeyDown={(e) => {
                const pressed = e.nativeEvent.code;
                if (pressed === 'Enter' && e.target.value.trim()) {
                  onSearch(e.target.value.trim());
                  setShowSearch(false);
                }
                if (pressed === 'Escape') setShowSearch(false);
              }}
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                fontSize: '14px',
                fontFamily: tokens.typography.fontFamily,
                background: 'transparent',
                paddingLeft: '14px'
              }}
            />
            <button
              onClick={() => { setQuery(''); setShowSearch(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
                border: 'none',
                padding: '4px',
                marginRight: '2px',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              <X size={16} color={tokens.colors.textSecondary} strokeWidth={2.5} />
            </button>
          </div>

          <button
            onClick={() => setShowSearch(!showSearch)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            <Search size={20} color={tokens.colors.textSecondary} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileActionLine;
